import { Link } from "react-router-dom";
import { Check, ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal } from "../components/Reveal";
import SeoHead from "../components/SeoHead";
import { OFFERS } from "../data/site";

const JOURNEY_STAGES = ["Understand", "Diagnose", "Prioritize", "Implement"];

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-[72px]">
      <SeoHead
        title="Services | Local Proof STL — Operational Performance Assessment"
        description="The Operational Performance Assessment: an evidence-based view of operational health — Business Health Scorecard, prioritized findings, 90-day roadmap, and executive readout. Implementation follows through the Operational Performance Partnership."
        canonical="https://localproofstl.com/services"
        ogType="website"
        ogUrl="https://localproofstl.com/services"
        ogImage="https://localproofstl.com/og-image.jpg"
        twitterImage="https://localproofstl.com/og-image.jpg"
      />
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
        <Reveal>
          <p className="eyebrow mb-8">Services</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
            An honest assessment, then disciplined implementation.
          </h1>
          <p className="mt-8 max-w-xl text-lg text-charcoal/80 leading-relaxed">
            Understand the business clearly, diagnose what is actually slowing it down, prioritize
            what deserves attention first — then implement.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <ol className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" data-testid="journey-line">
            {JOURNEY_STAGES.map((s, i) => (
              <li key={s} className="flex items-center gap-3">
                <span className="font-serif text-xl text-ink">{s}</span>
                {i < JOURNEY_STAGES.length - 1 && <span className="text-cloud">→</span>}
              </li>
            ))}
          </ol>
        </Reveal>
      </section>

      {/* Research callout — founder-approved copy (2026-09-23) */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-16">
        <Reveal>
          <div className="bg-surface border border-border rounded-2xl p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="eyebrow mb-4">Before we meet</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-ink mb-3">Every engagement begins before we meet.</h2>
              <p className="text-charcoal/70 leading-relaxed max-w-2xl">
                We research your business, market, and operating context before the first
                conversation — so we can spend less time gathering basics and more time
                understanding what may actually be constraining the business.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <span className="font-serif text-7xl lg:text-8xl text-cloud">00</span>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32 space-y-8">
        {OFFERS.map((s, i) => (
          <div key={s.slug}>
            {i === 1 && (
              <Reveal>
                <div className="flex items-center justify-center gap-3 py-4 text-steel" data-testid="services-flow-connector">
                  <ArrowDown size={16} />
                  <span className="text-xs uppercase tracking-widest">
                    Prioritized findings · 90-day roadmap · optional implementation
                  </span>
                  <ArrowDown size={16} />
                </div>
              </Reveal>
            )}
            <Reveal delay={0.04}>
              <div
                data-testid={`service-detail-${s.slug}`}
                className="bg-white border border-border rounded-2xl overflow-hidden grid lg:grid-cols-12"
              >
                <div className="lg:col-span-5 bg-ink text-white p-8 lg:p-12 relative overflow-hidden">
                  <div className="noise-overlay" />
                  <div className="relative">
                    <span className="text-mist text-sm font-semibold">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-serif text-3xl lg:text-4xl mt-4 mb-3 leading-tight">{s.name}</h2>
                    <p className="text-mist text-lg font-serif italic mb-6">{s.tagline}</p>
                    <p className="text-cloud/80 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
                <div className="lg:col-span-7 p-8 lg:p-12">
                  <div className="grid sm:grid-cols-2 gap-10">
                    <div>
                      <p className="eyebrow mb-5">Outcomes</p>
                      <ul className="space-y-3">
                        {s.outcomes.map((o) => (
                          <li key={o} className="flex gap-3 text-charcoal/80 text-[15px] leading-snug">
                            <Check size={17} className="text-steel mt-0.5 shrink-0" />
                            {o}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="eyebrow mb-5">Deliverables</p>
                      <ul className="space-y-3">
                        {s.deliverables.map((d) => (
                          <li key={d} className="text-charcoal/80 text-[15px] pb-3 border-b border-border">{d}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                    <div>
                      <p className="eyebrow mb-2">Ideal client</p>
                      <p className="text-charcoal/70 text-sm max-w-md leading-relaxed">{s.ideal}</p>
                    </div>
                    <Link
                      to="/contact"
                      data-track={`request-${s.slug}-services`}
                      data-testid={`service-cta-${s.slug}`}
                      className="group inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-charcoal transition-colors duration-200 shrink-0 w-fit"
                    >
                      {i === 0 ? "Request an Assessment" : "Start the Conversation"}
                      <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        ))}
      </section>
    </div>
  );
}
